'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  LIMITS,
  SITE_TYPES,
  calculate,
  formatMoney,
  normalizeInputs,
  toQueryString,
  type CalculatorConfig,
  type CalculatorInputs,
  type Currency,
  type SiteType,
} from '@/lib/tools/costCalculator';

const t = {
  en: {
    type: 'Type of website',
    types: {
      landing: ['Landing page', 'One focused page'],
      company: ['Company website', 'Several pages + contact'],
      ecommerce: ['Online store', 'Products, cart, checkout'],
      webapp: ['Web application', 'Custom portal or system'],
    } as Record<SiteType, [string, string]>,
    pages: 'Number of pages',
    languages: 'Languages',
    languagesHint: (n: number) => (n === 1 ? 'Single language' : `${n} languages (incl. Arabic RTL)`),
    store: 'Include an online store',
    storeIncluded: 'Included in the store package',
    integrations: 'Integrations',
    integrationsHint: 'Payment gateway, CRM, booking, WhatsApp, ERP…',
    result: 'Estimated price range',
    currencies: { EGP: 'Egyptian pound', SAR: 'Saudi riyal', AED: 'UAE dirham' } as Record<Currency, string>,
    note: 'Planning estimate — domain, hosting and content writing are not included.',
    share: 'Share this estimate',
    copy: 'Copy link',
    copied: 'Copied!',
    dec: 'Decrease',
    inc: 'Increase',
  },
  ar: {
    type: 'نوع الموقع',
    types: {
      landing: ['صفحة هبوط', 'صفحة واحدة مركّزة'],
      company: ['موقع شركة', 'عدة صفحات + تواصل'],
      ecommerce: ['متجر إلكتروني', 'منتجات وسلة ودفع'],
      webapp: ['تطبيق ويب', 'نظام أو بوابة مخصصة'],
    } as Record<SiteType, [string, string]>,
    pages: 'عدد الصفحات',
    languages: 'اللغات',
    languagesHint: (n: number) => (n === 1 ? 'لغة واحدة' : `${n} لغات (مع العربية من اليمين لليسار)`),
    store: 'إضافة متجر إلكتروني',
    storeIncluded: 'مشمول في باقة المتجر',
    integrations: 'التكاملات',
    integrationsHint: 'بوابة دفع، CRM، حجز، واتساب، ERP…',
    result: 'النطاق السعري التقديري',
    currencies: { EGP: 'جنيه مصري', SAR: 'ريال سعودي', AED: 'درهم إماراتي' } as Record<Currency, string>,
    note: 'تقدير تخطيطي — لا يشمل الدومين والاستضافة وكتابة المحتوى.',
    share: 'شارك هذا التقدير',
    copy: 'نسخ الرابط',
    copied: 'تم النسخ!',
    dec: 'تقليل',
    inc: 'زيادة',
  },
} as const;

const CURRENCIES: Currency[] = ['EGP', 'SAR', 'AED'];

interface CostCalculatorProps {
  locale: string;
  config: CalculatorConfig;
  initialInputs: CalculatorInputs;
  /** Absolute canonical URL of the tool page; the share link is this + the selections. */
  shareUrl: string;
  /** Embedded in an iframe: no URL syncing, tighter layout. */
  embed?: boolean;
}

function Stepper({
  id, label, hint, value, min, max, onChange, dec, inc,
}: {
  id: string; label: string; hint?: string; value: number; min: number; max: number;
  onChange: (n: number) => void; dec: string; inc: string;
}) {
  const btn =
    'h-10 w-10 shrink-0 rounded-lg border border-(--border) bg-(--bg) text-lg leading-none text-(--text) hover:border-primary-500 disabled:opacity-40 disabled:hover:border-(--border) transition-colors';
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-(--text) mb-2">{label}</label>
      <div className="flex items-center gap-2">
        <button type="button" className={btn} aria-label={`${dec}: ${label}`} disabled={value <= min} onClick={() => onChange(value - 1)}>−</button>
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="h-10 w-20 rounded-lg border border-(--border) bg-(--bg) text-center text-(--text) focus:outline-none focus:border-primary-500"
        />
        <button type="button" className={btn} aria-label={`${inc}: ${label}`} disabled={value >= max} onClick={() => onChange(value + 1)}>+</button>
      </div>
      {hint && <p className="mt-1.5 text-xs text-(--muted)">{hint}</p>}
    </div>
  );
}

export default function CostCalculator({ locale, config, initialInputs, shareUrl, embed = false }: CostCalculatorProps) {
  const labels = t[locale === 'ar' ? 'ar' : 'en'];
  const [inputs, setInputs] = useState<CalculatorInputs>(() => normalizeInputs(initialInputs));
  const [copied, setCopied] = useState(false);
  const firstRender = useRef(true);

  const result = useMemo(() => calculate(inputs, config), [inputs, config]);
  const link = `${shareUrl}?${toQueryString(inputs)}`;

  const update = (patch: Partial<CalculatorInputs>) => setInputs((cur) => normalizeInputs({ ...cur, ...patch }));

  // Keep the address bar in sync so the page URL is always the shareable one.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (!embed) window.history.replaceState(null, '', `?${toQueryString(inputs)}`);
  }, [inputs, embed]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the link is still visible in the field */
    }
  };

  const storeLocked = inputs.type === 'ecommerce';

  return (
    <div className={`rounded-2xl border border-(--border) bg-(--surface) ${embed ? 'p-4' : 'p-5 sm:p-7'}`}>
      <div className="grid gap-7 lg:grid-cols-2">
        {/* ── Inputs ─────────────────────────────────────────────────────── */}
        <div className="space-y-6">
          <fieldset>
            <legend className="text-sm font-medium text-(--text) mb-2">{labels.type}</legend>
            <div className="grid grid-cols-2 gap-2">
              {SITE_TYPES.map((type) => {
                const [name, desc] = labels.types[type];
                const active = inputs.type === type;
                return (
                  <label
                    key={type}
                    className={`cursor-pointer rounded-xl border p-3 transition-colors ${active ? 'border-primary-500 bg-primary-500/10' : 'border-(--border) hover:border-primary-500/50'}`}
                  >
                    <input
                      type="radio"
                      name={`site-type-${embed ? 'embed' : 'page'}`}
                      value={type}
                      checked={active}
                      onChange={() => update({ type })}
                      className="sr-only"
                    />
                    <span className="block text-sm font-semibold text-(--text)">{name}</span>
                    <span className="block text-xs text-(--muted) mt-0.5">{desc}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <Stepper
            id={`pages-${embed ? 'e' : 'p'}`} label={labels.pages} value={inputs.pages}
            min={LIMITS.pages.min} max={LIMITS.pages.max} onChange={(pages) => update({ pages })}
            dec={labels.dec} inc={labels.inc}
          />

          <fieldset>
            <legend className="text-sm font-medium text-(--text) mb-2">{labels.languages}</legend>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((n) => (
                <label
                  key={n}
                  className={`flex h-10 w-12 cursor-pointer items-center justify-center rounded-lg border text-sm font-medium transition-colors ${inputs.languages === n ? 'border-primary-500 bg-primary-500/10 text-(--text)' : 'border-(--border) text-(--muted) hover:border-primary-500/50'}`}
                >
                  <input
                    type="radio"
                    name={`languages-${embed ? 'embed' : 'page'}`}
                    value={n}
                    checked={inputs.languages === n}
                    onChange={() => update({ languages: n })}
                    className="sr-only"
                  />
                  {n}
                </label>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-(--muted)">{labels.languagesHint(inputs.languages)}</p>
          </fieldset>

          <label className={`flex items-start gap-3 ${storeLocked ? 'opacity-70' : 'cursor-pointer'}`}>
            <input
              type="checkbox"
              checked={inputs.ecommerce}
              disabled={storeLocked}
              onChange={(e) => update({ ecommerce: e.target.checked })}
              className="mt-1 h-4 w-4 rounded border-(--border) accent-primary-500"
            />
            <span>
              <span className="block text-sm font-medium text-(--text)">{labels.store}</span>
              {storeLocked && <span className="block text-xs text-(--muted)">{labels.storeIncluded}</span>}
            </span>
          </label>

          <Stepper
            id={`integrations-${embed ? 'e' : 'p'}`} label={labels.integrations} hint={labels.integrationsHint}
            value={inputs.integrations} min={LIMITS.integrations.min} max={LIMITS.integrations.max}
            onChange={(integrations) => update({ integrations })} dec={labels.dec} inc={labels.inc}
          />
        </div>

        {/* ── Result ─────────────────────────────────────────────────────── */}
        <div className="flex flex-col">
          <h2 className="text-sm font-medium text-(--muted) mb-3">{labels.result}</h2>
          <div className="space-y-3" aria-live="polite">
            {CURRENCIES.map((currency, i) => {
              const { min, max } = result.ranges[currency];
              return (
                <div
                  key={currency}
                  className={`rounded-xl border p-4 ${i === 0 ? 'border-primary-500/40 bg-primary-500/8' : 'border-(--border) bg-(--bg)'}`}
                >
                  <p className="text-xs text-(--muted)">{currency} · {labels.currencies[currency]}</p>
                  <p className={`mt-1 font-bold tabular-nums text-(--text) ${i === 0 ? 'text-2xl sm:text-3xl' : 'text-xl'}`} dir="ltr">
                    {formatMoney(min, locale)} – {formatMoney(max, locale)}
                  </p>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-(--muted)">{labels.note}</p>

          {!embed && (
            <div className="mt-5 border-t border-(--border) pt-4">
              <label htmlFor="share-link" className="block text-sm font-medium text-(--text) mb-2">{labels.share}</label>
              <div className="flex gap-2">
                <input
                  id="share-link"
                  readOnly
                  value={link}
                  dir="ltr"
                  onFocus={(e) => e.currentTarget.select()}
                  className="min-w-0 flex-1 rounded-lg border border-(--border) bg-(--bg) px-3 py-2 text-xs text-(--muted)"
                />
                <button
                  type="button"
                  onClick={copyLink}
                  className="shrink-0 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-neutral-900 hover:bg-primary-600 transition-colors"
                >
                  {copied ? labels.copied : labels.copy}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
